/**
 * Bridge between the assistant sphere and the page background. The sphere
 * publishes what it is doing and where it sits; the background reacts in CSS,
 * so nothing here runs per frame.
 */
export type AmbientState = "idle" | "listening" | "working" | "speaking";

export const AMBIENT_PULSE_EVENT = "ambient-pulse";
export type AmbientPulseDetail = { x: number; y: number };

const ANCHOR_SAMPLE_MS = 250;

export function publishAmbientState(state: AmbientState, root = document.documentElement) {
  if (root.dataset.assistantState !== state) root.dataset.assistantState = state;
}

export function clearAmbientState(root = document.documentElement) {
  delete root.dataset.assistantState;
}

/**
 * Keep `--ambient-x`/`--ambient-y` on the root at the element's center. The
 * sphere is draggable, so position is sampled at a low rate; the background
 * layers ease between samples. Returns a cleanup that restores the fallback.
 */
export function publishAmbientAnchor(element: Element) {
  const doc = element.ownerDocument;
  const win = doc.defaultView;
  const root = doc.documentElement;
  if (!win) return () => {};
  let lastX = Number.NaN;
  let lastY = Number.NaN;
  const sample = () => {
    if (doc.hidden || !element.isConnected) return;
    const rect = element.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    const x = Math.round(rect.left + rect.width / 2);
    const y = Math.round(rect.top + rect.height / 2);
    if (Math.abs(x - lastX) < 2 && Math.abs(y - lastY) < 2) return;
    lastX = x;
    lastY = y;
    root.style.setProperty("--ambient-x", `${x}px`);
    root.style.setProperty("--ambient-y", `${y}px`);
  };
  sample();
  const timer = win.setInterval(sample, ANCHOR_SAMPLE_MS);
  win.addEventListener("resize", sample);
  return () => {
    win.clearInterval(timer);
    win.removeEventListener("resize", sample);
    root.style.removeProperty("--ambient-x");
    root.style.removeProperty("--ambient-y");
  };
}

/** Ask the background to ripple from a viewport point (e.g. a window landing). */
export function pulseAmbient(x: number, y: number, target: Window | null = typeof window === "undefined" ? null : window) {
  if (!target || target.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  // Decorative only: a failure here must never interrupt the caller's animation.
  try {
    const EventCtor = (target as Window & { CustomEvent?: typeof CustomEvent }).CustomEvent ?? CustomEvent;
    target.dispatchEvent(new EventCtor<AmbientPulseDetail>(AMBIENT_PULSE_EVENT, { detail: { x, y } }));
  } catch {
    // ignore
  }
}
